import { useEffect } from "react";
import { useQuery } from "@tanstack/react-query";

import { useScheduleStore } from "@store/scheduleStore";
import * as req from "@script/req.ts";
import type { Version } from "./roster-craete/useRosterVersions";

/**
 * 초기 로스터 날짜 및 버전 설정
 * - 최신 로스터 정보를 가져와서 날짜 설정
 * - 해당 날짜의 버전 리스트에서 "issued" 버전 우선 선택
 * - "issued" 버전이 없으면 최신 버전 사용
 */
export const useRosterInitializer = ({
  groupId,
}: {
  groupId: string | null;
}) => {
  const { curDate, setCurDate, setCurScheduleInfo } = useScheduleStore();

  // 1. 최신 로스터 정보 조회 (날짜 정보 포함)
  const { data: initVersion, isSuccess: isInitVersionSuccess } = useQuery({
    queryKey: ["roster", "latest", groupId],
    queryFn: async () => {
      return await req.roster_latest(groupId);
    },
    enabled: !curDate, // 날짜가 없을 때만 실행
  });

  // console.log(initVersion);

  // 2. initVersion의 날짜로 버전 리스트 조회
  const { data: versionList, isSuccess: isVersionListSuccess } = useQuery<
    Version[]
  >({
    queryKey: [
      "roster",
      "versions",
      initVersion?.year,
      initVersion?.month,
      groupId,
    ],
    queryFn: async () => {
      if (!initVersion?.year || !initVersion?.month) return [];
      return await req.roster_versions(
        initVersion.year,
        initVersion.month,
        groupId
      );
    },
    enabled: !curDate && !!initVersion?.year && !!initVersion?.month, // initVersion이 로드되고 날짜가 없을 때만 실행
  });

  useEffect(() => {
    // 이미 날짜가 설정되어 있으면 실행하지 않음
    if (curDate) return;

    // initVersion이 로드되지 않았으면 대기
    if (!isInitVersionSuccess) return;

    // initVersion이 있으면 날짜와 버전 설정
    if (initVersion) {
      // versionList가 로드될 때까지 대기
      if (!isVersionListSuccess || !versionList) {
        return;
      }

      // 날짜 설정
      setCurDate({ year: initVersion.year, month: initVersion.month });

      // 버전 리스트에서 "issued" 버전 찾기
      const issuedVersions = versionList.filter(
        (item) => item.status === "issued"
      );

      if (issuedVersions.length > 0) {
        // "issued" 버전 중 최신 버전 선택 (version 번호가 높은 것)
        const latestIssued = [...issuedVersions].sort(
          (a, b) => b.version - a.version
        )[0];
        setCurScheduleInfo({
          scheduleId: latestIssued.schedule_id,
          version: latestIssued.version,
        });
      } else {
        // "issued" 버전이 없으면 initVersion 사용
        setCurScheduleInfo({
          scheduleId: initVersion.schedule_id,
          version: initVersion.version,
        });
      }
    } else {
      // initVersion이 없으면 오늘 날짜로 초기화
      const now = new Date();
      setCurDate({
        year: now.getFullYear(),
        month: now.getMonth() + 1,
      });
    }
  }, [
    curDate,
    isInitVersionSuccess,
    initVersion,
    isVersionListSuccess,
    versionList,
    setCurDate,
    setCurScheduleInfo,
  ]);

  // 현재 날짜 세팅 여부 반환
  return { isInitializing: !curDate };
};
